import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { downloadAuthenticatedFile, fetchAuthenticatedFile } from "../utils/authenticatedFileAccess";

const LINK_BUTTON_STYLE: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  font: "inherit",
};

function withDisposition(fileUrlBase: string, disposition: "attachment" | "inline"): string {
  const separator = fileUrlBase.includes("?") ? "&" : "?";
  return `${fileUrlBase}${separator}disposition=${disposition}`;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Could not open document.";
}

export function AuthenticatedFileLinks({
  fileUrlBase,
  fallbackFileName,
}: {
  fileUrlBase: string;
  fallbackFileName?: string;
}) {
  const titleId = useId();
  const [downloading, setDownloading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMimeType, setPreviewMimeType] = useState("");
  const [previewFileName, setPreviewFileName] = useState(fallbackFileName ?? "document");
  /** Bumped when a new blob URL is ready so the viewer mounts once per open (avoids iframe reload flicker). */
  const [previewGeneration, setPreviewGeneration] = useState(0);

  const previewUrlRef = useRef<string | null>(null);
  const previewUrl = previewUrlRef.current;

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    revokePreviewUrl();
    setPreviewGeneration((g) => g + 1);
  }, [revokePreviewUrl]);

  useEffect(() => {
    return () => revokePreviewUrl();
  }, [revokePreviewUrl]);

  useEffect(() => {
    if (!previewOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [previewOpen, closePreview]);

  const isImagePreview = previewMimeType.startsWith("image/");
  const isPdfPreview =
    previewMimeType === "application/pdf" ||
    previewMimeType.endsWith("+pdf") ||
    previewFileName.toLowerCase().endsWith(".pdf");

  const openPreview = useCallback(() => {
    setLoadingPreview(true);
    void fetchAuthenticatedFile(withDisposition(fileUrlBase, "inline"))
      .then(({ blob, fileName }) => {
        revokePreviewUrl();
        const typedBlob =
          blob.type && blob.type !== "application/octet-stream"
            ? blob
            : new Blob([blob], {
                type: fileName?.toLowerCase().endsWith(".pdf")
                  ? "application/pdf"
                  : fileName?.match(/\.(jpe?g|png|gif|webp)$/i)
                    ? `image/${fileName.split(".").pop()!.toLowerCase().replace("jpg", "jpeg")}`
                    : blob.type || "application/octet-stream",
              });
        previewUrlRef.current = URL.createObjectURL(typedBlob);
        setPreviewMimeType(typedBlob.type || "");
        setPreviewFileName(fileName || fallbackFileName || "document");
        setPreviewGeneration((g) => g + 1);
        setPreviewOpen(true);
      })
      .catch((error) => {
        window.alert(getErrorMessage(error));
      })
      .finally(() => setLoadingPreview(false));
  }, [fileUrlBase, fallbackFileName, revokePreviewUrl]);

  const previewModal =
    previewOpen && previewUrl ? (
      <div
        className="modal-backdrop modal-backdrop--doc-preview"
        role="presentation"
        onClick={closePreview}
      >
        <div
          className="modal modal--doc-preview"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="modal--doc-preview__header">
            <h3 id={titleId} className="modal--doc-preview__title">
              Document preview
            </h3>
            <button type="button" className="btn btn--sm" onClick={closePreview}>
              Close
            </button>
          </div>
          <p className="text-muted modal--doc-preview__filename">{previewFileName}</p>
          <div className="modal--doc-preview__body">
            {isImagePreview ? (
              <img
                key={`img-${previewGeneration}`}
                src={previewUrl}
                alt={previewFileName}
                className="modal--doc-preview__image"
              />
            ) : isPdfPreview ? (
              <object
                key={`pdf-${previewGeneration}`}
                className="modal--doc-preview__pdf"
                data={`${previewUrl}#toolbar=1&navpanes=0`}
                type="application/pdf"
                aria-label={previewFileName}
              >
                <iframe
                  title={previewFileName}
                  className="modal--doc-preview__pdf"
                  src={`${previewUrl}#toolbar=1`}
                />
              </object>
            ) : (
              <p className="text-muted">Preview is not available for this file type. Please use Download.</p>
            )}
          </div>
        </div>
      </div>
    ) : null;

  return (
    <>
      <button
        type="button"
        className="summary-card__link"
        style={LINK_BUTTON_STYLE}
        onClick={() => {
          setDownloading(true);
          void downloadAuthenticatedFile(withDisposition(fileUrlBase, "attachment"), fallbackFileName)
            .catch((error) => {
              window.alert(getErrorMessage(error));
            })
            .finally(() => setDownloading(false));
        }}
        disabled={downloading || loadingPreview}
      >
        {downloading ? "Downloading..." : "Download"}
      </button>
      <span className="text-muted"> · </span>
      <button
        type="button"
        className="summary-card__link"
        style={LINK_BUTTON_STYLE}
        onClick={openPreview}
        disabled={downloading || loadingPreview}
      >
        {loadingPreview ? "Opening..." : "View"}
      </button>
      {previewModal ? createPortal(previewModal, document.body) : null}
    </>
  );
}
